import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  CardActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoIcon from "@mui/icons-material/Photo";
import StarIcon from "@mui/icons-material/Star";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAlbum, useUpdateAlbum } from "../../hooks/useAlbums";
import type { ImageResponse } from "../../api/generated";

export const EditGalleryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: album, isLoading: albumLoading } = useAlbum(id || null);
  const updateAlbumMutation = useUpdateAlbum();
  const [images, setImages] = useState<ImageResponse[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<ImageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coverImage, setCoverImage] = useState<ImageResponse | null>(null);

  // Load gallery images
  useEffect(() => {
    const loadGalleryImages = async () => {
      if (!id) return;

      setLoadingImages(true);
      setError(null);
      try {
        if (!id) {
          setError("Gallery ID is required");
          return;
        }

        const { getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet } = await import("../../api/generated");
        const imagesResponse = await getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet({
          path: { album_id: id },
        });

        if (imagesResponse.data) {
          const coverImageId = album?.cover_image_id;
          const allImages = imagesResponse.data.items || [];
          // Find and set the cover image
          const cover = allImages.find((img: ImageResponse) => img.id === coverImageId);
          setCoverImage(cover || null);
          // Filter out the cover image from the list
          const galleryImages = allImages.filter(
            (img: ImageResponse) => img.id !== coverImageId
          );
          setImages(galleryImages);
        } else {
          setError("Failed to load gallery images");
        }
      } catch (err) {
        console.error("Failed to load gallery images:", err);
        setError("Failed to load gallery images");
      } finally {
        setLoadingImages(false);
      }
    };

    if (album?.id) {
      loadGalleryImages();
    }
  }, [id, album?.id, album?.cover_image_id]);

  const handleDeleteClick = (image: ImageResponse) => {
    setImageToDelete(image);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!imageToDelete || !album) return;

    try {
      // Get current image IDs excluding the one to delete
      const currentImageIds = images
        .map((img) => img.id)
        .filter((imgId) => imgId !== imageToDelete.id)
        .filter(Boolean) as string[];

      // Update the album with the new image list
      await updateAlbumMutation.mutateAsync({
        albumId: album.id,
        data: {
          image_ids: currentImageIds,
        },
      });

      // Remove from local state
      setImages((prev) => prev.filter((img) => img.id !== imageToDelete.id));
      setDeleteDialogOpen(false);
      setImageToDelete(null);
    } catch (err) {
      console.error("Failed to delete image:", err);
      setError("Failed to remove image from gallery");
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setImageToDelete(null);
  };

  const handleSetAsCover = async (image: ImageResponse) => {
    if (!album || !image.id) return;

    try {
      // Get current image IDs (excluding the new cover)
      const currentImageIds = images
        .map((img) => img.id)
        .filter((imgId) => imgId !== image.id)
        .filter(Boolean) as string[];

      // Add old cover image back to the list if it exists
      if (coverImage?.id) {
        currentImageIds.push(coverImage.id);
      }

      // Update the album with the new cover image
      await updateAlbumMutation.mutateAsync({
        albumId: album.id,
        data: {
          cover_image_id: image.id,
          image_ids: currentImageIds,
        },
      });

      // Update local state
      const oldCover = coverImage;
      setCoverImage(image);
      setImages((prev) => {
        const newImages = prev.filter((img) => img.id !== image.id);
        if (oldCover) {
          newImages.push(oldCover);
        }
        return newImages;
      });
    } catch (err) {
      console.error("Failed to set cover image:", err);
      setError("Failed to set cover image");
    }
  };

  if (albumLoading || loadingImages) {
    return (
      <AdminLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!album) {
    return (
      <AdminLayout>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">Gallery not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/galleries")}
            sx={{ mt: 2 }}
          >
            Back to Galleries
          </Button>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 4,
            gap: 2,
          }}
        >
          <IconButton
            onClick={() => navigate("/admin/galleries")}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              Edit Gallery: {album.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage images in this gallery. Click delete to remove an image.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Current Cover Image */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <StarIcon color="primary" />
            Cover Image
          </Typography>
          {coverImage ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box
                component="img"
                src={coverImage.cloudinary_url?.replace("/upload/", "/upload/w_200,h_200,c_fill/") || coverImage.cloudinary_url}
                alt={coverImage.title || "Cover image"}
                sx={{
                  width: 150,
                  height: 150,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: "primary.main",
                }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {coverImage.title || "Untitled"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This image is shown as the gallery thumbnail
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No cover image set. Select an image below to set it as the cover.
            </Typography>
          )}
        </Paper>

        {/* Images Grid */}
        {images.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No images in this gallery
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add images to this gallery from the gallery edit form.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 3,
            }}
          >
            {images.map((image) => {
              // Generate thumbnail URL
              const thumbnailUrl = image.cloudinary_url
                ? image.cloudinary_url.replace(
                    "/upload/",
                    "/upload/w_400,h_400,c_fill/"
                  )
                : undefined;

              return (
                <Card
                  key={image.id}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={thumbnailUrl || image.cloudinary_url}
                    alt={image.title || "Gallery image"}
                    sx={{
                      height: 250,
                      objectFit: "cover",
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {image.title || "Untitled"}
                    </Typography>
                    {image.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {image.description}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ pt: 0, justifyContent: "space-between" }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleSetAsCover(image)}
                      size="small"
                      title="Set as cover image"
                      disabled={updateAlbumMutation.isPending}
                      sx={{
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.contrastText",
                        },
                      }}
                    >
                      <PhotoIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(image)}
                      size="small"
                      sx={{
                        "&:hover": {
                          backgroundColor: "error.light",
                          color: "error.contrastText",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Remove Image from Gallery?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove this image from the gallery? The
              image will not be deleted, only removed from this gallery.
            </Typography>
            {imageToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {imageToDelete.title || "Untitled"}
                </Typography>
                {imageToDelete.cloudinary_url && (
                  <Box
                    component="img"
                    src={imageToDelete.cloudinary_url.replace(
                      "/upload/",
                      "/upload/w_300,h_300,c_fill/"
                    )}
                    alt={imageToDelete.title || "Image"}
                    sx={{
                      width: "100%",
                      maxWidth: 300,
                      height: "auto",
                      borderRadius: 1,
                      mt: 1,
                    }}
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={updateAlbumMutation.isPending}
            >
              {updateAlbumMutation.isPending ? "Removing..." : "Remove"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
